import { eventKeys } from "@/lib/eventKeys";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useGetEventBus from "../useGetEventBus";

const AWAITING_MAILS_TIMEOUT_MS = 120_000;

type LocationState = {
    awaitingMails?: boolean;
};

const useCustomViewAwaitingMails = (
    customViewId: string,
    mailsCount: number,
    isFetching: boolean,
) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [awaitingMails, setAwaitingMails] = useState(
        () => (location.state as LocationState | null)?.awaitingMails ?? false,
    );

    const clearAwaitingMailsState = useCallback(() => {
        setAwaitingMails(false);

        if ((location.state as LocationState | null)?.awaitingMails) {
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.pathname, location.state, navigate]);

    // CustomViewEmails is not remounted when the :viewId param changes, so the
    // useState initializer above only captures the flag for the very first
    // view. Re-sync whenever a navigation carries the awaitingMails flag — e.g.
    // creating another view while already on a custom-view route.
    useEffect(() => {
        if ((location.state as LocationState | null)?.awaitingMails) {
            setAwaitingMails(true);
        }
    }, [location.state]);

    const handleCustomViewListUpdated = useCallback(
        ({ customViewId: updatedViewId }: { customViewId: string }) => {
            if (updatedViewId === customViewId) {
                clearAwaitingMailsState();
            }
        },
        [clearAwaitingMailsState, customViewId],
    );

    useGetEventBus(
        eventKeys.customView.listUpdated,
        handleCustomViewListUpdated,
    );

    useEffect(() => {
        if (mailsCount > 0) {
            clearAwaitingMailsState();
        }
    }, [clearAwaitingMailsState, mailsCount]);

    useEffect(() => {
        if (!awaitingMails) {
            return undefined;
        }

        const timeoutId = window.setTimeout(
            () => clearAwaitingMailsState(),
            AWAITING_MAILS_TIMEOUT_MS,
        );

        return () => window.clearTimeout(timeoutId);
    }, [awaitingMails, clearAwaitingMailsState]);

    return {
        // Keep the skeleton up while the view's first mails load and while we
        // wait for a freshly created view to be populated (including the
        // refetch triggered by the list-updated event).
        showSkeleton: mailsCount === 0 && (awaitingMails || isFetching),
        // Only a genuinely awaiting (newly created) view shows the
        // "view is being set up" message — never a normal initial load.
        isConfiguring: mailsCount === 0 && awaitingMails,
    };
};

export default useCustomViewAwaitingMails;
